import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { usefulLinksAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { toArray } from '../utils/apiData';

const UsefulLinksContext = createContext(null);

export const useUsefulLinks = () => {
  const context = useContext(UsefulLinksContext);
  if (!context) {
    throw new Error('useUsefulLinks must be used within a UsefulLinksProvider');
  }
  return context;
};

export const UsefulLinksProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadLinks = useCallback(async () => {
    try {
      const response = await usefulLinksAPI.getAll();
      // The sidebar maps over this, and the provider sits above the route-level
      // ErrorBoundary — a non-array here would take the whole app down, not one page.
      setLinks(toArray(response.data, 'useful links'));
    } catch (error) {
      console.error('Error loading useful links:', error);
      // Fall back to an empty list so the sidebar simply omits the section
      // rather than breaking the whole navigation.
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // This provider wraps the whole app, including the login page, but
  // GET /api/useful-links is jwt_required. Firing it while logged out earns a
  // 401 for no benefit, so wait until there is a session.
  useEffect(() => {
    if (isAuthenticated) {
      loadLinks();
    } else {
      setLinks([]);
      setLoading(false);
    }
  }, [isAuthenticated, loadLinks]);

  const value = {
    links,
    loading,
    loadLinks,
  };

  return <UsefulLinksContext.Provider value={value}>{children}</UsefulLinksContext.Provider>;
};
